import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { loginWithGitHub, verifyOAuthState } from '../../services/OAuthService';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const OAuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOAuthCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Check for errors
      if (errorParam) {
        setError(t(TRANSLATIONS.OAUTH_CALLBACK_ERROR_PREFIX, { error: errorParam }) as string);
        setLoading(false);
        return;
      }

      // GitHub callback
      if (code && state) {
        // Verificar state para prevenir CSRF
        if (!verifyOAuthState('github', state)) {
          setError(translate(t, TRANSLATIONS.OAUTH_CALLBACK_CSRF_ERROR));
          setLoading(false);
          return;
        }

        // Enviar código al backend
        const response = await loginWithGitHub(code);

        // Login con tokens
        login(response.accessToken, response.refreshToken);

        // Redirigir al dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
        return;
      }

      // Si llegamos aquí, no hay parámetros válidos
      setError(translate(t, TRANSLATIONS.OAUTH_CALLBACK_INVALID));
      setLoading(false);
    } catch (error: any) {
      console.error('OAuth callback error:', error);
      setError(error.message || translate(t, TRANSLATIONS.OAUTH_CALLBACK_ERROR_FALLBACK));
      setLoading(false);
    }
  };

  const handleRetry = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          size="large"
        />
        <p style={{ marginTop: 24, fontSize: 16, color: '#666' }}>
          {translate(t, TRANSLATIONS.OAUTH_CALLBACK_COMPLETING)}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Result
          status="error"
          title={translate(t, TRANSLATIONS.OAUTH_CALLBACK_FAILED_TITLE)}
          subTitle={error}
          extra={[
            <Button type="primary" key="retry" onClick={handleRetry}>
              {translate(t, TRANSLATIONS.OAUTH_CALLBACK_BACK_TO_LOGIN)}
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}>
      <Result
        status="success"
        title={translate(t, TRANSLATIONS.OAUTH_CALLBACK_SUCCESS_TITLE)}
        subTitle={translate(t, TRANSLATIONS.OAUTH_CALLBACK_REDIRECTING)}
      />
    </div>
  );
};

export default OAuthCallback;
